import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { isSuperAdmin, isGroupAdmin, getDisplayRole } from '../../utils/roles';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  related_entity_id?: string;
}

const NotificationsPage = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');

  const isSuperAdminUser = isSuperAdmin(userProfile?.role);
  const userRole = getDisplayRole(userProfile?.role);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      setupRealtimeSubscription();
    }
  }, [user, filter]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);

      // Apply filter
      if (filter === 'unread') {
        query = query.eq('is_read', false);
      } else if (filter === 'read') {
        query = query.eq('is_read', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedNotifications: Notification[] = (data || []).map((n: any) => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        timestamp: new Date(n.created_at),
        read: n.is_read || false,
        related_entity_id: n.related_entity_id,
      }));

      setNotifications(formattedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user) return;

    const channel = supabase
      .channel('notifications-page')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationPath = (notification: Notification): string | null => {
    switch (notification.type) {
      case 'payment_submitted':
        if (userProfile?.role === 'super_admin') {
          return '/payment-processing';
        }
        return null;
      
      case 'payment_approved':
      case 'payment_rejected':
        if (userProfile?.role === 'group_admin' || userProfile?.role === 'member') {
          return '/subscription-management';
        }
        return null;
      
      case 'payment_pending':
        // Check if this is a contribution approval notification (title contains "Saving Approval")
        // or if it's a subscription payment notification
        if (notification.title && notification.title.includes('Saving Approval')) {
          // This is a contribution approval notification - route to contribution recording
          // Allow for group_admin, super_admin, or assigned admins (group_members.role='admin')
          const isAdmin = userProfile?.role === 'group_admin' || 
                         userProfile?.role === 'super_admin' || 
                         userProfile?.group_roles?.includes('admin');
          if (isAdmin) {
            // Include contribution ID and notification flag in URL
            const params = new URLSearchParams();
            if (notification.related_entity_id) {
              params.set('contributionId', notification.related_entity_id);
            }
            params.set('from', 'notification');
            return `/contribution-recording?${params.toString()}`;
          }
          return null;
        }
        // Otherwise, it's a subscription payment notification
        if (userProfile?.role === 'group_admin' || userProfile?.role === 'member') {
          return '/subscription-management';
        }
        return null;
      
      case 'contribution':
      case 'approval':
      case 'new_contribution':
      case 'contribution_approved':
      case 'contribution_rejected':
      case 'contribution_pending':
        return '/contribution-recording';
      
      case 'member_joined':
      case 'member_left':
      case 'member_added':
      case 'admin_assigned':
        if (userProfile?.role === 'group_admin' || userProfile?.role === 'super_admin') {
          return '/group-management';
        }
        return '/member-dashboard';
      
      case 'loan_approved':
      case 'loan_repayment':
        if (userProfile?.role === 'group_admin' || userProfile?.role === 'super_admin') {
          return '/admin-dashboard';
        }
        return '/member-dashboard';
      
      default:
        return null;
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    handleMarkAsRead(notification.id);
    const path = getNotificationPath(notification);
    if (path) {
      navigate(path);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'payment_submitted':
      case 'payment_pending':
        return 'Clock';
      case 'payment_approved':
        return 'CheckCircle';
      case 'payment_rejected':
        return 'XCircle';
      case 'new_contribution':
      case 'contribution_approved':
      case 'contribution_rejected':
      case 'contribution_pending':
        return 'DollarSign';
      case 'member_joined':
      case 'member_left':
      case 'member_added':
        return 'Users';
      case 'loan_approved':
      case 'loan_repayment':
        return 'CreditCard';
      case 'admin_assigned':
        return 'UserCheck';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'payment_submitted':
      case 'payment_pending':
        return 'var(--color-warning)';
      case 'payment_approved':
        return 'var(--color-success)';
      case 'payment_rejected':
        return 'var(--color-destructive)';
      case 'new_contribution':
      case 'contribution_approved':
        return 'var(--color-success)';
      case 'contribution_rejected':
        return 'var(--color-destructive)';
      case 'contribution_pending':
        return 'var(--color-warning)';
      case 'member_joined':
      case 'member_added':
        return 'var(--color-success)';
      case 'member_left':
        return 'var(--color-muted-foreground)';
      case 'loan_approved':
      case 'loan_repayment':
        return 'var(--color-primary)';
      case 'admin_assigned':
        return 'var(--color-warning)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      <Helmet>
        <title>Notifications - communitySaving</title>
      </Helmet>

      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      <div className="flex">
        <div className="hidden lg:block">
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            userRole={userRole}
            isSuperAdmin={isSuperAdminUser}
          />
        </div>

        <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 max-w-4xl mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-2">
                  Notifications
                </h1>
                <p className="text-muted-foreground">
                  {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
                </p>
              </div>
              {unreadCount > 0 && (
                <Button
                  onClick={handleMarkAllAsRead}
                  variant="outline"
                  iconName="CheckCircle"
                >
                  Mark all as read
                </Button>
              )}
            </div>

            {/* Filter buttons */}
            <div className="mb-6 flex gap-2">
              {(['all', 'unread', 'read'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                    filter === f
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            {/* Notifications list */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading notifications...</div>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <Icon name="Bell" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  {filter === 'unread' 
                    ? 'No unread notifications' 
                    : filter === 'read'
                    ? 'No read notifications'
                    : 'No notifications'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`
                      bg-card border border-border rounded-md p-4
                      transition-interactive cursor-pointer
                      ${notification.read ? '' : 'bg-primary/5 border-primary/20'}
                      hover:bg-muted
                    `}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${getNotificationColor(notification.type)}15` }}
                      >
                        <Icon
                          name={getNotificationIcon(notification.type)}
                          size={20}
                          color={getNotificationColor(notification.type)}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-foreground">
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {formatTimestamp(notification.timestamp)}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(notification.id);
                            }}
                            className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                            title="Delete notification"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default NotificationsPage;

